import React, { useContext } from 'react';

import { useFormikContext } from 'formik';

import { LAG_DAYS_LIMIT, LinkedActivityCell, LinkedActivityDates } from './linkedActivity';

import IconButton from 'CommonComponents/IconButton';

import ProjectPlanContext from 'Context/project_plan/ProjectPlanContext';

import TrashIcon from 'Assets/icons/trash.svg';

const PredecessorRow = ({ row, index, readOnly, disabled, onRemove }) => {
    const { dependencyTypes } = useContext(ProjectPlanContext);
    const { errors, setFieldValue } = useFormikContext();

    // A message for the whole list is a string; the tab shows it above the table.
    const rowErrors = (Array.isArray(errors?.dependencies) && errors.dependencies[index]) || {};
    const rowError = rowErrors.predecessor_id || rowErrors.type || rowErrors.lag_days;

    return (
        <tr>
            <td>
                <LinkedActivityCell activity={row.activity} />
                {!!rowError && <div className="tw-text-red-500 tw-text-sm">{rowError}</div>}
            </td>
            <td>
                <select
                    className="select-input tw-border-0"
                    aria-label={$t('Type')}
                    value={row.type}
                    onChange={(e) => setFieldValue(`dependencies.${index}.type`, e.target.value)}
                    disabled={readOnly || disabled}
                >
                    {Object.entries(dependencyTypes).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
            </td>
            <td>
                <input
                    type="number"
                    className={rowErrors.lag_days ? 'text-input tw-border tw-border-red-500' : 'text-input tw-border-0'}
                    aria-label={$t('Lag (days)')}
                    min={-LAG_DAYS_LIMIT}
                    max={LAG_DAYS_LIMIT}
                    step={1}
                    value={row.lag_days}
                    onChange={(e) => setFieldValue(`dependencies.${index}.lag_days`, e.target.value)}
                    disabled={readOnly || disabled}
                />
            </td>
            <td>
                <LinkedActivityDates activity={row.activity} />
            </td>
            <td>
                {!readOnly && (
                    <IconButton
                        ghost
                        variant="danger"
                        size="sm"
                        aria-label={$t('Remove')}
                        title={$t('Remove')}
                        onClick={() => onRemove(index)}
                        disabled={disabled}
                    >
                        <TrashIcon className="tw-w-4 tw-h-4" />
                    </IconButton>
                )}
            </td>
        </tr>
    );
};

const MaskedPredecessorRow = ({ link }) => (
    <tr>
        <td>
            <LinkedActivityCell activity={link.activity} />
        </td>
        <td></td>
        <td></td>
        <td>
            <LinkedActivityDates activity={link.activity} />
        </td>
        <td></td>
    </tr>
);

/**
 * Predecessors staged in the form, then those the user may not see, which are kept as they are.
 */
const PredecessorsTable = ({ rows, maskedPredecessors, readOnly, disabled, onRemove }) => {
    const empty = rows.length === 0 && maskedPredecessors.length === 0;

    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table tw-table-fixed">
                <thead>
                    <tr>
                        <th>{$t('Activity')}</th>
                        <th className="tw-w-40">{$t('Type')}</th>
                        <th className="tw-w-24">{$t('Lag (days)')}</th>
                        <th className="tw-w-48">{$t('Predecessor dates')}</th>
                        <th className="tw-w-12"></th>
                    </tr>
                </thead>
                <tbody>
                    {empty && (
                        <tr>
                            <td colSpan={5} className="tw-text-center tw-text-charcoal-60">
                                {$t('No predecessors. This activity can start any time.')}
                            </td>
                        </tr>
                    )}
                    {rows.map((row, index) => (
                        <PredecessorRow key={row.predecessor_id} row={row} index={index} readOnly={readOnly} disabled={disabled} onRemove={onRemove} />
                    ))}
                    {maskedPredecessors.map((link) => (
                        <MaskedPredecessorRow key={link.id} link={link} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default PredecessorsTable;
